import { Component, OnInit } from "@angular/core";
import { HttpClient } from "@app/shared/services/http/http-client.service";
import { ToastrService } from "ngx-toastr";
import { DriverService } from "@app/driver/shared/driver.service";
import { authenticationService } from "@app/login/auth.service";
import { Permissions } from "@models/permissions.enum";
import { SchedulingTeam, Board, SchedulingTeamDriver, UnassignedDriver, SchedulingTeamContact } from "@app/shared/models/Board.model";
import { AssignDriverResult } from "@app/shared/components/assign-driver-modal/assign-driver-modal.component";

interface ClientGroup {
  clientId: string;
  clientName: string;
  schedulingTeams: SchedulingTeam[];
}

@Component({
  selector: "app-boards",
  templateUrl: "./boards.component.html",
  styleUrls: ["./boards.component.css"],
})
export class BoardsComponent implements OnInit {
  schedulingTeams: SchedulingTeam[] = [];
  clientGroups: ClientGroup[] = [];
  expandedSchedulingTeamIds: Set<string> = new Set();
  isLoading: boolean = false;
  searchText: string = "";
  filteredClientGroups: ClientGroup[] = [];
  
  // Unassigned drivers
  unassignedDrivers: UnassignedDriver[] = [];
  filteredUnassignedDrivers: UnassignedDriver[] = [];

  // Assign / edit driver modal (delegated to AssignDriverModalComponent)
  showAssignModal = false;
  assignModalDriverId: string | null = null;
  assignModalExistingTeamId: string | null = null;
  assignModalExistingBoardId: string | null = null;
  assignModalExistingBillToId: string | null = null;

  constructor(
    private http: HttpClient,
    private toastr: ToastrService,
    private driverService: DriverService,
    private authService: authenticationService
  ) {}

  ngOnInit() {
    this.loadAllSchedulingTeams();
    this.loadUnassignedDrivers();
  }

  loadAllSchedulingTeams() {
    this.isLoading = true;
    this.http.get("api/board/listAll").subscribe({
      next: (schedulingTeams: any) => {
        this.schedulingTeams = schedulingTeams || [];
        this.groupSchedulingTeamsByClient();
        this.filterSchedulingTeams();
        this.isLoading = false;
      },
      error: (error) => {
        this.toastr.error("Error loading scheduling teams");
        console.error(error);
        this.isLoading = false;
      },
    });
  }

  loadUnassignedDrivers() {
    this.http.get("api/board/unassignedDrivers").subscribe({
      next: (drivers: any) => {
        this.unassignedDrivers = drivers || [];
        this.filterUnassignedDrivers();
      },
      error: (error) => {
        console.error("Error loading unassigned drivers", error);
      },
    });
  }

  groupSchedulingTeamsByClient() {
    const clientMap = new Map<string, ClientGroup>();
    
    this.schedulingTeams.forEach(schedulingTeam => {
      if (!clientMap.has(schedulingTeam.clientId)) {
        clientMap.set(schedulingTeam.clientId, {
          clientId: schedulingTeam.clientId,
          clientName: schedulingTeam.clientName || 'Unknown Client',
          schedulingTeams: []
        });
      }
      clientMap.get(schedulingTeam.clientId)!.schedulingTeams.push(schedulingTeam);
    });
    
    this.clientGroups = Array.from(clientMap.values()).sort((a, b) => 
      a.clientName.localeCompare(b.clientName)
    );
  }

  filterSchedulingTeams() {
    if (!this.searchText || this.searchText.trim() === "") {
      this.filteredClientGroups = [...this.clientGroups];
      this.filterUnassignedDrivers();
      return;
    }

    const searchLower = this.searchText.toLowerCase().trim();
    this.filteredClientGroups = this.clientGroups
      .map(clientGroup => {
        const filteredSchedulingTeams = clientGroup.schedulingTeams.filter(
          (schedulingTeam) =>
            (schedulingTeam.name && schedulingTeam.name.toLowerCase().includes(searchLower)) ||
            (schedulingTeam.clientName && schedulingTeam.clientName.toLowerCase().includes(searchLower)) ||
            (schedulingTeam.contacts && schedulingTeam.contacts.some(
              (contact) =>
                (contact.name && contact.name.toLowerCase().includes(searchLower)) ||
                (contact.email && contact.email.toLowerCase().includes(searchLower)) ||
                (contact.role && contact.role.toLowerCase().includes(searchLower))
            )) ||
            (schedulingTeam.boards && schedulingTeam.boards.some(
              (board) =>
                board.name && board.name.toLowerCase().includes(searchLower)
            )) ||
            (schedulingTeam.drivers && schedulingTeam.drivers.some(
              (d) => this.driverMatchesSearch(d, searchLower)
            ))
        );
        
        return {
          ...clientGroup,
          schedulingTeams: filteredSchedulingTeams
        };
      })
      .filter(clientGroup => clientGroup.schedulingTeams.length > 0);
    
    this.filterUnassignedDrivers();
  }

  private driverMatchesSearch(driver: SchedulingTeamDriver, searchLower: string): boolean {
    const fullName = `${driver.name || ''} ${driver.lastName || ''}`.toLowerCase().trim();
    const reverseName = `${driver.lastName || ''} ${driver.name || ''}`.toLowerCase().trim();
    
    return (driver.name && driver.name.toLowerCase().includes(searchLower)) ||
           (driver.lastName && driver.lastName.toLowerCase().includes(searchLower)) ||
           (driver.driverNo && driver.driverNo.toLowerCase().includes(searchLower)) ||
           fullName.includes(searchLower) ||
           reverseName.includes(searchLower) ||
           (driver.boardName && driver.boardName.toLowerCase().includes(searchLower)) ||
           (driver.billToClientName && driver.billToClientName.toLowerCase().includes(searchLower));
  }

  private unassignedDriverMatchesSearch(driver: UnassignedDriver, searchLower: string): boolean {
    const fullName = `${driver.name || ''} ${driver.lastName || ''}`.toLowerCase().trim();
    const reverseName = `${driver.lastName || ''} ${driver.name || ''}`.toLowerCase().trim();
    
    return (driver.name && driver.name.toLowerCase().includes(searchLower)) ||
           (driver.lastName && driver.lastName.toLowerCase().includes(searchLower)) ||
           (driver.driverNo && driver.driverNo.toLowerCase().includes(searchLower)) ||
           fullName.includes(searchLower) ||
           reverseName.includes(searchLower);
  }

  filterUnassignedDrivers() {
    if (!this.searchText || this.searchText.trim() === "") {
      this.filteredUnassignedDrivers = [...this.unassignedDrivers];
      return;
    }

    const searchLower = this.searchText.toLowerCase().trim();
    this.filteredUnassignedDrivers = this.unassignedDrivers.filter(
      (driver) => this.unassignedDriverMatchesSearch(driver, searchLower)
    );
  }

  toggleSchedulingTeamExpansion(schedulingTeamId: string) {
    if (this.expandedSchedulingTeamIds.has(schedulingTeamId)) {
      this.expandedSchedulingTeamIds.delete(schedulingTeamId);
    } else {
      this.expandedSchedulingTeamIds.add(schedulingTeamId);
    }
  }

  isSchedulingTeamExpanded(schedulingTeamId: string): boolean {
    return this.expandedSchedulingTeamIds.has(schedulingTeamId);
  }

  openAssignDriverModal(driver: UnassignedDriver) {
    this.assignModalDriverId = driver.id;
    this.assignModalExistingTeamId = null;
    this.assignModalExistingBoardId = null;
    this.assignModalExistingBillToId = null;
    this.showAssignModal = true;
  }

  openEditDriverModal(schedulingTeam: SchedulingTeam, driver: SchedulingTeamDriver) {
    this.assignModalDriverId = driver.driverId;
    this.assignModalExistingTeamId = schedulingTeam.id!;
    this.assignModalExistingBoardId = driver.boardId || null;
    this.assignModalExistingBillToId = driver.billToClientId || null;
    this.showAssignModal = true;
  }

  onAssignSuccess(result: AssignDriverResult) {
    this.loadAllSchedulingTeams();
    this.loadUnassignedDrivers();
  }

  onUpdateSuccess() {
    this.loadAllSchedulingTeams();
  }

  onUnassignSuccess() {
    this.loadAllSchedulingTeams();
    this.loadUnassignedDrivers();
  }

  getDriverDisplay(driver: SchedulingTeamDriver): string {
    let display = `${driver.driverNo} - ${driver.name} ${driver.lastName}`;
    const parts = [];
    if (driver.boardName) parts.push(driver.boardName);
    if (driver.billToClientName) parts.push(`Bill To: ${driver.billToClientName}`);
    if (parts.length > 0) display += ` (${parts.join(', ')})`;
    return display;
  }

  getDriverNameDisplay(driver: SchedulingTeamDriver): string {
    return `${driver.driverNo} - ${driver.name} ${driver.lastName}`;
  }

  getDriverNameHighlighted(driver: SchedulingTeamDriver): string {
    const driverName = this.getDriverNameDisplay(driver);
    return this.highlightText(driverName);
  }

  getUnassignedDriverNameDisplay(driver: UnassignedDriver): string {
    return `${driver.driverNo} - ${driver.name} ${driver.lastName}`;
  }

  getUnassignedDriverNameHighlighted(driver: UnassignedDriver): string {
    const driverName = this.getUnassignedDriverNameDisplay(driver);
    return this.highlightText(driverName);
  }

  getBoardsForSchedulingTeam(schedulingTeam: SchedulingTeam): Board[] {
    return schedulingTeam.boards || [];
  }

  highlightText(text: string): string {
    if (!text) return "";
    if (!this.searchText || this.searchText.trim() === "") return text;

    const searchTerm = this.searchText.trim();
    const regex = new RegExp(`(${this.escapeRegex(searchTerm)})`, "gi");
    return text.replace(regex, '<mark class="search-highlight">$1</mark>');
  }

  private escapeRegex(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }

  hasPermission(permissionName: string): boolean {
    const permission = Permissions[permissionName];
    return this.authService.hasPermission(permission);
  }

  getContactDisplay(contact: SchedulingTeamContact): string {
    let display = contact.name;
    const parts = [];
    if (contact.role) parts.push(contact.role);
    if (contact.email) parts.push(contact.email);
    if (contact.phone) parts.push(contact.phone);
    if (contact.doNotContact) parts.push('⚠️ Do Not Contact');
    if (parts.length > 0) display += ` - ${parts.join(', ')}`;
    return display;
  }
}